import * as React from 'react';

import { ITheme, useKeytipRef } from '@fluentui/react';
import { useMergedRefs } from '@fluentui/react-hooks';
import { ISpinButton, SpinButton } from '@fluentui/react/lib/SpinButton';
import { KeyCodes } from '@fluentui/react/lib/Utilities';

import { dataUniqueId, generateAriaDescription, getInputMethodFromEvent, InputMethod } from '@1js/acui-common';
import { AppContext } from '@1js/acui-context';
import { checkExperiment, ribbonExperiments } from '@1js/acui-experiments';
import { focusableDiv, getMemoizedInputProps } from '@1js/acui-focusable-div';
import { iconNameToIconProps } from '@1js/acui-icons';
import { defaultKeytipExecute, deriveInputKeytip } from '@1js/acui-keytip';
import { AppThemeConsumer } from '@1js/acui-theme';
import { AppTooltipHost, getTooltipText } from '@1js/acui-tooltip';

import { AppSpinButtonInternalProps, AppSpinButtonUnsetValue, AppSpinButtonValue } from './AppSpinButton.Props';
import { getArrowButtonStyles, getSpinButtonStyles, spinButtonOuterContainerStyles } from './AppSpinButton.styles';
import {
  formatSpinButtonValue,
  getNewSpinValue,
  getUnitFromUnitType,
  isValueWithinUnitRange,
  parseSpinButtonValue,
  tryGetReservedValue,
  tryGetReservedValueIndex,
} from './SpinButtonUnitParser';

export interface AppSpinButtonState {
  focused: boolean;
}
export class AppSpinButtonBase extends React.PureComponent<AppSpinButtonInternalProps, AppSpinButtonState> {
  private spinButtonRef = React.createRef<HTMLDivElement>();
  private spinButton = React.createRef<ISpinButton>();
  private tooltipHost: AppTooltipHost | undefined;

  // This allows us to access AppContext directly from this.context
  static contextType: any = AppContext;
  declare context: React.ContextType<typeof AppContext>;
  public state = {
    focused: false,
  };

  public render() {
    // We want to allow 'blank' behaviour in the the spinButton.
    // All other values must be formatted.
    const value: string =
      this.props.value === AppSpinButtonUnsetValue
        ? this.props.value
        : formatSpinButtonValue(
            this.props.value,
            this.props.unitType,
            this.props.validUnits,
            this.props.numberFormatInfo,
            this.props.reservedValue
          );
    const unit = getUnitFromUnitType(this.props.unitType, this.props.validUnits);
    const { ariaDescriptionId, ariaDescriptionElement } = generateAriaDescription(
      this.props.id,
      this.props.customTooltip
    );
    const uniqueId = dataUniqueId(this.props);

    return (
      <AppThemeConsumer>
        {(theme: ITheme) => {
          const arrowButtonStyles = getArrowButtonStyles(theme, this.props.buttonStyles);
          return (
            <div style={spinButtonOuterContainerStyles} data-unique-id={uniqueId}>
              <SpinButton
                ref={useMergedRefs(
                  useKeytipRef<HTMLDivElement>({
                    keytipProps: deriveInputKeytip(this.props.keytipProps, this.keytipExecute),
                  }),
                  this.spinButtonRef
                )}
                componentRef={this.spinButton}
                label={this.props.label}
                labelPosition={this.props.labelPosition}
                iconProps={iconNameToIconProps(this.props.icon, this.props.iconColor)}
                disabled={this.props.disabled}
                onDecrement={this.onDecrement}
                onIncrement={this.onIncrement}
                onValidate={this.onValidate}
                value={value}
                onFocus={this.onFocus}
                onBlur={this.onBlur}
                styles={getSpinButtonStyles(
                  this.props.styles,
                  theme,
                  !!this.props.disabled,
                  this.state.focused,
                  this.props.labelPosition
                )}
                upArrowButtonStyles={arrowButtonStyles}
                downArrowButtonStyles={arrowButtonStyles}
                incrementButtonIcon={iconNameToIconProps(this.props.incrementButtonIcon)}
                decrementButtonIcon={iconNameToIconProps(this.props.decrementButtonIcon)}
                incrementButtonAriaLabel={this.props.incrementAriaLabel}
                decrementButtonAriaLabel={this.props.decrementAriaLabel}
                ariaPositionInSet={this.props.positionInSet}
                ariaSetSize={this.props.setSize}
                ariaValueNow={this.props.value === AppSpinButtonUnsetValue ? undefined : this.props.value}
                ariaValueText={value}
                keytipProps={deriveInputKeytip(this.props.keytipProps, this.keytipExecute)}
                title={undefined}
                ariaDescribedBy={ariaDescriptionElement && ariaDescriptionId}
                data-unique-id={`${uniqueId}-input`}
                inputProps={getMemoizedInputProps(this.context, this.props.dataNav)}
                min={unit ? unit.minValue : undefined}
                max={unit ? unit.maxValue : undefined}
              />
              {ariaDescriptionElement}
            </div>
          );
        }}
      </AppThemeConsumer>
    );
  }

  public componentDidMount() {
    if (this.context.setTooltipElement && this.context.updateTooltipsHidden) {
      this.tooltipHost = new AppTooltipHost({
        updateTooltipsHidden: this.context.updateTooltipsHidden,
        setTooltipElement: this.context.setTooltipElement,
        refElement: this.spinButtonRef,
        controlId: this.props.id,
        controlLabel: getTooltipText(false /* hideLabel */, this.props.label, this.props.customTooltip),
        tooltipDirection: this.props.tooltipDirection,
        performSetOnNextFrame: !!checkExperiment(this.context, ribbonExperiments.delayRenderTooltipsIsEnabled),
      });
      this.tooltipHost.registerTooltipHostActions();
    }
  }

  public componentWillUnmount() {
    if (this.tooltipHost) {
      this.tooltipHost.unregisterTooltipHostActions();
    }
  }

  public componentDidUpdate(prevProps: AppSpinButtonInternalProps) {
    if (
      this.tooltipHost &&
      (prevProps.customTooltip !== this.props.customTooltip || prevProps.label !== this.props.label)
    ) {
      this.tooltipHost.updateTooltip(getTooltipText(false /* hideLabel */, this.props.label, this.props.customTooltip));
    }
  }

  private onFocus = () => {
    this.setState({ focused: true });
  };

  private onBlur = () => {
    this.setState({ focused: false });
  };

  public focus() {
    this.spinButton.current && this.spinButton.current.focus();
  }

  private keytipExecute = (executeTarget: HTMLElement) => {
    defaultKeytipExecute(executeTarget);
  };

  private onDecrement = (
    valueWithUnits: string,
    event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
  ) => {
    return this.spin(valueWithUnits, false /*spinningUp*/, event);
  };

  private onIncrement = (
    valueWithUnits: string,
    event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
  ) => {
    return this.spin(valueWithUnits, true /*spinningUp*/, event);
  };

  private spin(
    valueWithUnits: string,
    spinningUp: boolean,
    event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
  ) {
    let { value: newValue, unitType: newUnitType } = this.props;

    // Spinning below the minimum value starts spinning between reserved values. If valid reserved values are ["Ana", "Bob", "Charlie"]
    // the spinning works in the following way
    // <-Spinning Down -- Spinning Up ->
    // [Charlie, Bob, Ana, minValue... (spinning using step) ... maxValue]
    if (valueWithUnits === AppSpinButtonUnsetValue) {
      return undefined;
    }
    let newReservedValue = undefined;
    const reservedValueIndex =
      this.props.validReservedValues !== undefined
        ? tryGetReservedValueIndex(valueWithUnits, this.props.validReservedValues)
        : -1;
    if (this.props.validReservedValues !== undefined && reservedValueIndex >= 0) {
      // The Spin Button is displaying a reserved value. We need to handle the spinning in a different way
      if (spinningUp) {
        // User is spinning up, so let's find the new value
        if (reservedValueIndex > 0) {
          // The reserved value has a previous value, then return it.
          newReservedValue = this.props.validReservedValues[reservedValueIndex - 1];
        } else {
          // The reserved value is the first one in the list. Spinning up should go to the minimum numerical value.
          const unit = getUnitFromUnitType(this.props.unitType, this.props.validUnits);
          if (unit !== undefined) {
            newValue = unit.minValue;

            // If customNormalize is provided, call it with the newValue to get the normalized value from the user
            if (this.props.customNormalizeValue) {
              newValue = this.props.customNormalizeValue(newValue, unit);
            }
          }
        }
      } else {
        // User is spinning down, looking for the new value
        if (reservedValueIndex < this.props.validReservedValues.length - 1) {
          // We are not in the last reserved value, we still can return the next one
          newReservedValue = this.props.validReservedValues[reservedValueIndex + 1];
        } else {
          // We are in the last reserved value, we should keep the same and do nothing
          return undefined;
        }
      }
    } else {
      // The Spin button is displaying a numeric value with units. Then, parse value and units.

      // Try to parse the value and units from the Spin Button
      const { value, unit } = parseSpinButtonValue(
        valueWithUnits,
        this.props.validUnits,
        this.props.numberFormatInfo,
        this.props.unitType
      );

      if (isNaN(value) || unit === undefined) {
        return undefined;
      }

      if (
        this.props.validReservedValues !== undefined &&
        this.props.validReservedValues.length > 0 &&
        !spinningUp &&
        value === unit.minValue
      ) {
        // In case we are at the minimum value and user is spinning down. Go to the first reserved value if any.
        newReservedValue = this.props.validReservedValues[0];
      } else {
        // Get the new value depending if user is spinning up or down.
        newValue = getNewSpinValue(
          value,
          unit,
          spinningUp,
          this.props.numberFormatInfo,
          this.props.customNormalizeValue
        );
        newUnitType = unit.unitType;
      }
    }

    // Create the new string that will be displayed
    const newValueWithUnits =
      newReservedValue !== undefined
        ? newReservedValue
        : formatSpinButtonValue(newValue, newUnitType, this.props.validUnits, this.props.numberFormatInfo);

    this.executeCommand(
      newValue,
      newUnitType,
      newReservedValue,
      false /* shouldTakeFocus */,
      getInputMethodFromEvent(event)
    ); // keep focus when decrementing, incrementing or spinning
    return newValueWithUnits;
  }

  private onValidate = (newValue: string, event?: React.SyntheticEvent<HTMLElement>) => {
    // onValidate callback is only invoked in the React SpinButton control to validate manually typed values
    // This validation gets triggered by a blur event or certain keyboard events like enter or tab.
    // Hitting tab is an explicit focus navigation to the next control in a commanding surface, so
    // here we check for that to determine if the commanding surface should keep focus
    const keyboardEvent: React.KeyboardEvent<HTMLElement> = event as React.KeyboardEvent<HTMLElement>;
    let shouldTakeFocus: boolean = keyboardEvent && keyboardEvent.which ? keyboardEvent.which !== KeyCodes.tab : false;
    if (this.props.shouldTakeFocus !== undefined) {
      shouldTakeFocus = this.props.shouldTakeFocus;
    }

    if (newValue.trim() === AppSpinButtonUnsetValue) {
      return undefined;
    }
    // First check if user entered any reserved value
    if (this.props.validReservedValues !== undefined) {
      const newReservedValue = tryGetReservedValue(newValue, this.props.validReservedValues);
      if (newReservedValue !== undefined) {
        this.executeCommand(
          this.props.value,
          this.props.unitType,
          newReservedValue,
          shouldTakeFocus,
          InputMethod.Keyboard
        );
        return newReservedValue;
      }
    }

    // Get the current value between a reserved value or value + unit
    const reservedValue =
      this.props.reservedValue !== undefined && this.props.validReservedValues !== undefined
        ? tryGetReservedValue(this.props.reservedValue, this.props.validReservedValues)
        : undefined;

    const currentValueWithUnits =
      reservedValue !== undefined
        ? reservedValue
        : formatSpinButtonValue(
            this.props.value,
            this.props.unitType,
            this.props.validUnits,
            this.props.numberFormatInfo
          );

    // If there is no change in the string, return undefined so SpinButton doesn't update the value
    if (newValue === currentValueWithUnits) {
      return undefined;
    }

    const { value, unit } = parseSpinButtonValue(
      newValue,
      this.props.validUnits,
      this.props.numberFormatInfo,
      this.props.unitType
    );
    let newValueWithUnits = undefined;

    if (unit == null || !isValueWithinUnitRange(value, unit)) {
      // if user input is invalid input or out of range, set the new value to the current one
      newValueWithUnits = currentValueWithUnits;
    } else {
      newValueWithUnits = formatSpinButtonValue(
        value,
        unit.unitType,
        this.props.validUnits,
        this.props.numberFormatInfo
      );

      // Only call onExecute if the new value is valid and different from current value
      this.executeCommand(value, unit.unitType, undefined, shouldTakeFocus, InputMethod.Keyboard);
    }

    return newValueWithUnits;
  };

  private executeCommand(
    value: AppSpinButtonValue,
    unitType: string,
    specialString?: string,
    shouldTakeFocus?: boolean,
    inputMethod?: InputMethod
  ) {
    if (this.props.onExecute) {
      this.props.onExecute({
        id: this.props.id,
        value,
        unitType,
        reservedValue: specialString,
        location: this.props.location,
        // Bug 2575409: Changing height/width of a picture with styles that is located in a table causes a crash
        // as a result, shouldTakeFocus will be set to false until the bug is fixed.
        shouldTakeFocus: shouldTakeFocus && false,
        inputMethod,
      });
    }
  }
}

export const AppSpinButton = focusableDiv<AppSpinButtonInternalProps, {}>(AppSpinButtonBase);
